// ---- بابا ----
// The boy's father, pottering about his mother's garden while she feeds his
// son. He does not serve, he does not eat and he is not spelled: he walks, he
// stops and looks at the sofra, and now and then he tells Danny صحتين. That is
// the whole of him, and it is on purpose — the round already has two people
// carrying beats in it, and a third with a job would be a third thing for a
// three-year-old to track.
//
// His model is the fourth out of the same pipeline as jiddo.glb, tata.glb and
// boy.glb, and came rigged the same way with one difference: the bones arrived
// Mixamo-named (`mixamorig:Hips`), which the shared loader does not read. They
// are plain names in baba.glb, as the other three ship — so core/humanoid.ts
// reads him unchanged.
//
// He shipped Walking, Running and the single-frame `baselayer` — no idle, which
// is the hand his mother was dealt too (tata.ts). Held, that frame is a clean
// neutral stand, so it is his idle with a slow breath written over the top. His
// Running clip is loaded and never asked for: nothing he does is urgent.
//
// Another family's father came out of another rigger, with names of his own
// for a walk and an idle (CAST.babaClips, pack.ts), and an idle that is not a
// held frame but nine seconds of stretching. He stands on its first frame,
// which is also where it ends, with the same breath over it; and now and then
// a stop is the whole stretch, played through (STRETCH, below).
//
// ---- and he is the one who gets out of the way ----
//
// This is the whole of how he fits, and it is not a detail. The boy's run at
// the table was soaked over 40,000 approaches (see the README) to get one
// number right: that he ALWAYS arrives, round a table and round a strolling
// grandmother. Both of those he slides along by name (`stepTo`, boy.ts).
// Putting a third grown-up in his path would either be a man he walks straight
// through, or — if he were taught to avoid one — a fourth body in the wedge
// that took all that measuring to clear, and the one failure a child cannot tap
// past is a boy stuck against a table for the rest of the game.
//
// So neither of them knows he is there. HE keeps clear of THEM, every frame,
// and of the table, and of the edges of the field; the boy runs his measured
// line and his father steps aside, which is also what actually happens in a
// garden when a child is running for food.
import * as THREE from 'three';
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js';
import {asset} from '../../core/assets';
import {clipMs,has,sayPhrase,speaking} from '../../core/audio';
import {elder,inBounds,intoBounds} from '../../core/field';
import {Animator,readHumanoid} from '../../core/humanoid';
import {cam,scene} from '../../core/scene';
import {clamp,rnd,turnToward} from '../../core/util';
import {boy} from './boy';
import {pack} from './pack';
import {waiting} from './field';
import {SOFRA,edge,legClears,ring} from './set';

export const baba=new THREE.Group(); scene.add(baba);
// A grown man, a touch taller than his mother (2.25) and well over his son
// (1.35) — the three of them read as three generations rather than as a set.
export const BABA_H=2.3;
// Danny's father's names for his clips; another family's father's pack says
// what his are called (CAST.babaClips), and loadBaba reads them before anything plays.
let WALK='Walking', IDLE='Armature|clip0|baselayer';
let anim:Animator|null=null;
let walkStride=2.2;                              // what the clip measures out at; this only stands in if it is missing
let lifts:Record<string,number>={}, ground=0;
let idleMs=0;                                    // how long his idle runs, played through: one frame for Danny's father

export function loadBaba(){
  const {baba:file,babaClips:own}=pack().CAST;
  if(own){WALK=own.walk;IDLE=own.idle;}
  new GLTFLoader().loadAsync(asset(file)).then(g=>{
    const m=g.scene, rig=readHumanoid(m,BABA_H,g.animations,file,'baba'); if(!rig)return;
    baba.add(m); ground=m.position.y; const a=new Animator(m,g.animations,IDLE);
    lifts=a.lifts([...rig.feet,...rig.toes]);   // before anything plays: it measures against the rest pose
    walkStride=a.stride(WALK,rig.toes,walkStride);
    idleMs=a.ms(IDLE);
    anim=a;
  }).catch(err=>console.warn(file+' did not load',err));
}

// ---- where he walks ----
// A ring about the table, outside his mother's orbit (0.55–1.55 off its edge)
// and outside the line the boy eats at (0.32 off it), so he is never standing
// where the food is going or where his son is coming to eat it. Off the EDGE,
// as hers is (edge, set.ts), so it is as wide past an end as past a long side.
const RING_IN=1.65, RING_OUT=3.45;
// And where on that ring is asked of the CAMERA, not of an angle. His mother
// keeps to a band of radians off the away-from-the-camera line (tata.ts) and
// that rule does not survive being lent to him, because his ring reaches more
// than twice as far out as hers. The field runs from z -8.5 to 3.0 with the table at
// 1.2, so there are seven units of grass on one side of it and 1.8 on the other:
// drag the camera round to look from the far side and "the half away from the
// lens" is the cramped one, where nothing on his ring is in the field at all.
// Measured, that is what happened — every draw failed, he fell through to the
// go-round fallback, which knows about the table and the bounds and nothing
// about the picture, and he spent 76% of the round off the edge of the screen.
//
// So the two things actually wanted are asked directly, and they hold at any
// yaw and any pinch:
//   `seen`   — he projects inside the frame, so a man put in the garden to be
//              watched is on the screen.
//   `behind` — he is no nearer the lens than the sofra is. That is the whole of
//              what the far-side rule was ever for: at 3.2 units in FRONT of the
//              table he is five from the camera and half the frame tall, looming
//              over the food. Behind it he is smaller than she is and rides up
//              the picture, which is where the room is.
const CLEAR=0.5;                                 // how near the table's edge he or a leg of his may come
const LEG_MIN=1.1;                               // a stroll is at least this far, or it is a shuffle on the spot
const PAUSE_MIN=900, PAUSE_MAX=2600;             // between strolls: a look at the sofra
// An idle longer than a pause is a little scene, and a pause is too short to
// see it: started at every stop, it would be a stretch begun and abandoned
// every time he walked on. So he stands on its first frame, and one stop in four
// is the whole of it instead, the stop as long as the clip. Never two running:
// a man who stretches at every stop is not a man taking a breather.
const STRETCH=0.25;
const SPEED=1.05;                                // units a second, an amble — he is not going anywhere
// How much room he leaves the two of them. The boy's own APART is 1.05 and the
// README's note on why it is not 0.85 applies twice over here: two GROWN-UPS at
// arm's length merge into one silhouette from the camera, which is the opposite
// of what a third person is for.
const OFF_TATA=1.55, OFF_BOY=1.25;
// The field, a half-stride in from the engine's bounds. Nothing clamps him but
// this — he is not a `Body`, so `separate()` never sees him — which is the same
// bargain his mother has (tata.ts).
const IN=0.5, inField=(x:number,z:number)=>inBounds(x,z,IN);

type State='pause'|'walk';
let state:State='pause', next=2200, tx=0, tz=0, walkT=0, phase=0;
let head=0, rot=0, sayAt=Infinity, sayUntil=-1e9, lastSaid=-1e9, quietUntil=0;
let stretchUntil=-1e9, stretched=false;          // the stop that is a stretch runs until then; and was the last one?
export const babaDebug={get state(){return state},get clip(){return anim?.name??''},get model(){return anim?.model??null},
  get target(){return {x:tx,z:tz}},get stride(){return walkStride},get lifts(){return lifts},
  get sayAt(){return sayAt},get said(){return lastSaid},get stretchUntil(){return stretchUntil}};

// Where he projects in the frame. His chest rather than his feet: he is 2.3
// tall, so a man whose shoes are behind the tray is still a man on the screen,
// and testing the ground under him would send him away from the near rail for
// no reason. Margins wider than the engine's `inFrame`, which is for a plate
// landing on a table and wants it clear of the tray — he only has to be in shot.
const NDC=new THREE.Vector3();
const seen=(x:number,z:number)=>{NDC.set(x,1.5,z).project(cam);
  return Math.abs(NDC.x)<0.86&&NDC.y>-0.75&&NDC.y<0.92;};
// No nearer the lens than the sofra, give or take a stride, so abeam of it
// counts and in front of it does not.
const behind=(x:number,z:number)=>
  Math.hypot(x-cam.position.x,z-cam.position.z)>Math.hypot(SOFRA.x-cam.position.x,SOFRA.z-cam.position.z)-0.5;
// Which half of the PICTURE something is in — left or right of the middle,
// which is the only sense in which two people can be said to be on the same
// side of a table when the camera is free to swing round it.
const half=(x:number,z:number)=>{NDC.set(x,1.5,z).project(cam);return NDC.x>=0?1:-1;};

// Somewhere else on the ring, or null — which is the caller's cue to stand for
// another beat rather than to enter 'walk' for a spot he is already on. That
// one frame of "walking" is visible: it pins the clip at time 0 and eases out
// of a mid-stride pose (tata.ts learned this the hard way).
//
// The draws go all the way round the ring and the picture sorts them, in tiers.
// The first spot that is both in shot and behind the table wins outright — the
// same bargain the serve makes with the table top (field.ts), and what keeps him
// moving about the garden rather than converging on one best corner of it.
// Under that, the best of what was merely legal, because a man who can find
// nowhere stands still, and a statue in a garden where everything else moves is
// worse than a man briefly at the edge of the shot.
//
// He also prefers the half of the frame his mother is not in, so the two of them
// stand either side of the sofra rather than stacking up on one edge of it. A
// preference and not a rule: one pass wants it, the next does not, because a man
// who will only stand on one side of a garden is a man who stands still.
type Draw={x:number;z:number;d:number}|null;
const further=(a:Draw,b:Draw)=>!a||(!!b&&b.d>a.d)?b:a;
function spot(){
  const p=baba.position;
  const hers=half(elder.position.x,elder.position.z);
  // What is kept when no draw is both in shot and behind the table — which is
  // not a rare case but half of all camera angles. The field is 1.8 units deep
  // past the sofra on one side and seven on the other, so swing the lens round
  // to the shallow side and "behind the table" is off the grass entirely.
  // Keeping the FIRST legal draw instead put him dead in front of the lens 93%
  // of the round at one angle; keeping the one FURTHEST FROM THE CAMERA puts
  // him as far back as the grass goes, which abeam of the table is fine and in
  // front of it is what there was to avoid. The same lesson the serve learned
  // about a crowded table: the roomiest of the good draws, not the first of all
  // of them (serveSpot, field.ts).
  let onScreen:Draw=null, legal:Draw=null;
  for(const wantSide of [true,false])for(let i=0;i<20;i++){
    const {x,z}=ring(rnd(0,Math.PI*2),rnd(RING_IN,RING_OUT));
    if(!inField(x,z))continue;
    if(Math.hypot(x-p.x,z-p.z)<LEG_MIN)continue;                     // somewhere NEW, or it is not a walk
    if(Math.hypot(x-elder.position.x,z-elder.position.z)<OFF_TATA)continue;
    if(legClears(p.x,p.z,x,z)<CLEAR)continue;                        // or the leg would brush the sofra
    const d={x,z,d:Math.hypot(x-cam.position.x,z-cam.position.z)};
    legal=further(legal,d);
    if(!seen(x,z))continue;
    onScreen=further(onScreen,d);
    if(!behind(x,z))continue;
    if(wantSide&&half(x,z)===hers)continue;
    return {x,z};                                  // in shot and behind it: taken as drawn, which is what moves him about
  }
  const fall=onScreen||legal;
  if(fall)return {x:fall.x,z:fall.z};
  // Nothing at all in forty draws: he is boxed in. So he goes ROUND the table a
  // step at a time instead, the way she does — and only ever to somewhere in the
  // field, since the version of her fallback that checked the table and not the
  // bounds walked her out of the field and parked her there for good.
  const ae=Math.atan2(p.x-SOFRA.x,p.z-SOFRA.z), re=edge(p.x,p.z);
  const RS=[clamp(re,RING_IN+0.2,RING_OUT-0.2),RING_IN+0.4,RING_OUT-0.4];
  for(let k=1;k<=12;k++)for(const s of [1,-1])for(const r of RS){
    const {x,z}=ring(ae+s*k*0.3,r);
    if(!inField(x,z))continue;
    if(Math.hypot(x-p.x,z-p.z)<0.4)continue;
    if(Math.hypot(x-elder.position.x,z-elder.position.z)<OFF_TATA)continue;
    if(legClears(p.x,p.z,x,z)<CLEAR)continue;
    return {x,z};
  }
  return null;
}
// Out of everyone, off the sofra and inside the field — every frame, whatever
// moved him. This is the yield: he is the only one of the three who does it,
// and it is what lets the other two carry on knowing nothing about him.
//
// The order matters. The two people first, because those are the pushes that
// can put him somewhere he may not be; then the table and the bounds, which are
// the two invariants — and the table LAST of the two, by going round the ring
// when a step straight out of it would leave the field. Pushed off his mother
// toward the near edge, a radial shove out of the sofra and a clamp back into
// the field can argue with each other for ever; sliding round the ring cannot.
function yield_(){
  const p=baba.position;
  // Two passes, for the reason the engine's own shuffle gives for taking three:
  // pushing clear of one of them can push him into the other, and one pass of
  // two pushes in a fixed order does not converge (separate(), core/field.ts).
  // Measured: stepping clean off his son and straight into his mother happened
  // on one probe in twenty-seven of being dropped on top of one of them, and the
  // second pass halves it. Not three, and not five — five measures the same as
  // two, because what is left is a limit cycle between the two circles rather
  // than an overshoot that damps, and it is only reachable by putting him
  // exactly on top of somebody, which the game has no way to do: he holds his
  // distance every frame, and the fastest anyone closes on him is the boy's
  // running 3.4 a second, or 0.03 of a unit in a frame.
  for(let pass=0;pass<2;pass++)
  for(const [q,d] of [[boy.position,OFF_BOY],[elder.position,OFF_TATA]] as [THREE.Vector3,number][]){
    const dx=p.x-q.x, dz=p.z-q.z, h=Math.hypot(dx,dz);
    if(h<d&&h>1e-4){p.x=q.x+dx/h*d;p.z=q.z+dz/h*d;}
    else if(h<=1e-4){p.x=q.x+d;}                 // exactly on top of them: any direction will do
  }
  intoBounds(p,IN);
  if(edge(p.x,p.z)>=CLEAR)return;
  const dx=p.x-SOFRA.x, dz=p.z-SOFRA.z, r=Math.hypot(dx,dz);
  // Round the ring to a bearing that is on the grass — and, if there is one, to
  // a bearing that is also clear of the two of them, because this slide runs
  // AFTER the pushes above and can otherwise undo them: her orbit comes to 0.55
  // off the edge and this is 0.5, so a slide onto the bearing she is standing on
  // puts him 5cm from his own mother, and the next frame pushes him back in.
  // Built to happen rather than caught happening — 62,570 probes of being
  // shoved about the sofra never once reached it through play, and pinning her
  // on the ring reaches it every time.
  //
  // Clear of them is a want and the grass is a must: somewhere on the sofra's
  // rim is always in the field, and nowhere he can be is worth a frame off it.
  const a0=r>1e-6?Math.atan2(dx,dz):0;
  for(const roomy of [true,false])for(let k=0;k<25;k++)for(const s of (k?[1,-1]:[1])){
    const {x,z}=ring(a0+s*k*0.26,CLEAR);
    if(!inField(x,z))continue;
    if(roomy&&(Math.hypot(x-elder.position.x,z-elder.position.z)<OFF_TATA
            || Math.hypot(x-boy.position.x,z-boy.position.z)<OFF_BOY))continue;
    p.x=x; p.z=z; return;
  }
}
// ---- صحتين داني ----
// What you say to somebody who is eating, and he says it to his son by name.
// Now and then, which is the point of him: a garden with someone in it who
// might say something is a livelier garden than one with a man on a timer.
//
// Only ever over a mouthful, because that is the moment the words are FOR:
// Danny eats, says what it was like, and his father tells him صحتين over the
// top of the empty plate. And not over every one. He used to have an urge of
// his own besides, every ten to twenty-odd seconds, that the mouthful only
// pulled forward — and since a round is about that long, the first family to
// hear him heard it after nearly every dish, and between dishes too, said to
// nobody eating anything. Nobody had heard it before that: Danny's father has
// no take yet, so here he had only ever walked about in silence. Now, once he
// has said it, a bite does not prompt him again for a random 35 to 60 seconds.
// Measured over three minutes at a tap every 2.5 seconds, that is 4 times
// against 19 dishes, where the old urge said it 17 times; a slower speller
// hears it after more of his dishes, and at random which.
//
// And he waits for a gap. `sayPhrase` stops whatever line is running to make
// room for its own (core/audio.ts), so a father who spoke the moment the urge
// took him would cut his mother off mid-تفضل — the one rule every voice in this
// house keeps is that the grown-ups do not talk over each other. So when it is
// time and somebody is speaking he simply asks again in a moment, which lands
// him in the quiet after his son's يمّ rather than losing the turn altogether.
//
// Recorded only, like every line in the house. With no take he walks about
// saying nothing, and that is the whole of what a missing clip costs.
const QUIET_MIN=35000, QUIET_MAX=60000;          // after he has said it, how long before a bite can prompt him again
const RE_ASK=400;                                // it is his turn but the field is not quiet: ask again
// The boy has just picked up a dish (game.ts hands this on from the round's own
// count). Not a command to speak — the gap after his son's line is still what
// decides it — but the only thing that ever gives him the urge, and only once
// he has been quiet long enough.
export const babaSaw=(t:number)=>{if(t>=quietUntil)sayAt=Math.min(sayAt,t+2200);};
function maybeSay(t:number){
  if(t<sayAt)return;
  const {SAHTEIN}=pack();
  if(!has('R'+SAHTEIN)){sayAt=Infinity;return;}  // no take: the urge passes, as it always has in Danny's
  if(speaking(t)){sayAt=t+RE_ASK;return;}
  sayPhrase(SAHTEIN);
  lastSaid=t; sayUntil=t+clipMs('R'+SAHTEIN); sayAt=Infinity; quietUntil=t+rnd(QUIET_MIN,QUIET_MAX);
  // He says it TO him: he stops where he is and turns to his son, which is the
  // difference between a man talking to a child and a man talking to a tree.
  head=Math.atan2(boy.position.x-baba.position.x,boy.position.z-baba.position.z);
  state='pause'; next=t+700;
}
function brain(t:number,dt:number){
  if(!anim)return false;
  maybeSay(t);
  // His own mouth, and nothing else's. The engine's `talking()` is every voice
  // in the house at once, and gating on it is the mistake that parked his
  // mother at the rim for whole rounds (tata.ts) — a father who froze through
  // his son's five-second يمّ would be the same statue.
  if(t<sayUntil){if(state==='walk')state='pause';next=Math.max(next,t+400);return false;}
  if(state==='pause'){
    // Standing still, he watches whatever she has just put out — the same thing
    // she watches, and the thing his son is on his way to. The sofra itself
    // when every dish has been eaten.
    const out=waiting();
    if(out)head=Math.atan2(out.position.x-baba.position.x,out.position.z-baba.position.z);
    else head=Math.atan2(SOFRA.x-baba.position.x,SOFRA.z-baba.position.z);
    if(t<next||t<stretchUntil)return false;
    const s=spot();
    if(!s){next=t+600;return false;}             // boxed in: another beat, not a frame of 'walk'
    tx=s.x; tz=s.z; state='walk'; walkT=t; phase=0; return false;
  }
  const p=baba.position, dx=tx-p.x, dz=tz-p.z, d=Math.hypot(dx,dz);
  // There, or long enough that something has been in his way the whole time —
  // his son runs through this ring and the yield above can hold him off a spot
  // he is trying to reach, so arriving is never assumed.
  if(d<0.25||t-walkT>9000){
    state='pause'; next=t+rnd(PAUSE_MIN,PAUSE_MAX);
    stretched=!stretched&&idleMs>PAUSE_MAX&&Math.random()<STRETCH;
    if(stretched)stretchUntil=t+idleMs;
    return false;
  }
  head=Math.atan2(dx,dz);
  const v=Math.min(d,SPEED*dt);
  p.x+=dx/d*v; p.z+=dz/d*v;
  phase+=v/walkStride;
  return true;
}
export function updateBaba(t:number,dt:number){
  const walking=brain(t,dt);
  yield_();                                      // after the step, so what shows is never inside anybody
  if(anim){
    const w=walking?WALK:IDLE; if(w!==anim.name)anim.play(w,0.28);
    // Standing, the idle is held on its first frame, unless this stop is the
    // stretch: play() started it at 0 as he came to a halt, and it runs.
    const held=anim.name===IDLE&&t>=stretchUntil;
    anim.update(dt,anim.name===WALK?phase:held?0:undefined);
    rot=turnToward(rot,head,4*dt); baba.rotation.y=rot;
    // Each clip is authored at its own height (clipLifts, core/humanoid.ts), and
    // mid-crossfade what shows is a weighting of two poses — so the drop is that
    // same weighting (lift), rather than a chase after the incoming one.
    // The breath goes on the GROUP and the clip's own height on the model, so
    // the two never fight: held, his idle is one frame, and without a breath over
    // it he is a statue in the middle of a garden where everything else moves.
    anim.model.position.y=ground-anim.lift(lifts);
    baba.position.y=held?0.02*Math.sin(t*0.0019):0;
    baba.rotation.z=held?0.007*Math.sin(t*0.0015):0;
  }
}
